import React, { useEffect, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import api, { normalize } from '../services/api';
import { consultarCnpj, camposDaConsultaCnpj } from '../services/cnpj';
import { InputCepCustom, InputCnpjCpfCustom, InputTelefoneCustom, formatCnpjCpf, formatTelefone } from '../lanz-ui';
import { richItemTemplate, richValueTemplate } from './RichSelectOption';
import './RichSelectOption.css';
import { buildFilialFilterOptions, labelEmpresaFilial } from '../utils/filtroHelpers';
import '../pages/EmpresaPage.css';
import '../pages/FiliaisPage.css';
import '../pages/AgentesPage.css';

const TIPOS = [
  { label: 'Cliente', value: 'Cliente' },
  { label: 'Fornecedor', value: 'Fornecedor' },
];

const TIPOS_PESSOA = [
  { label: 'Pessoa jurídica (CNPJ)', value: 'Juridica' },
  { label: 'Pessoa física (CPF)', value: 'Fisica' },
];

function digitsOnly(v) {
  return String(v || '').replace(/\D/g, '');
}

function inferPessoa(cnpjcpf, pessoaAtual) {
  const d = digitsOnly(cnpjcpf);
  if (d.length === 11) return 'Fisica';
  if (d.length === 14) return 'Juridica';
  return pessoaAtual || 'Juridica';
}

function emptyForm(defaultTipo, defaultFilialId) {
  return {
    id: null,
    tipo: defaultTipo || 'Cliente',
    pessoa: 'Juridica',
    razaosocial: '',
    fantasia: '',
    cnpjcpf: '',
    email: '',
    emailsAdicionais: '',
    telefone: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    codigoIbgeMunicipio: '',
    inscricaoMunicipal: '',
    inscricaoEstadual: '',
    retemImpostosFederais: false,
    filialId: defaultFilialId == null || defaultFilialId === '' ? 0 : defaultFilialId,
  };
}

/**
 * Dialog reutilizável de cadastro/edição de agente (cliente/fornecedor) — mesmo
 * formulário usado em AgentesPage, para ser reaproveitado em outros fluxos
 * (ex.: criação rápida de tomador na emissão de NFS-e).
 *
 * Props:
 * - visible, onHide: controle de exibição.
 * - onSaved(agente): chamado após salvar com sucesso, com o agente retornado pela API.
 * - toastRef: ref de <Toast/> do chamador, usado para mensagens de sucesso/erro.
 * - defaultTipo: tipo pré-selecionado ao criar (padrão 'Cliente').
 * - defaultFilialId: filial pré-selecionada ao criar (0/null = matriz).
 * - agente: quando informado, o dialog abre em modo edição (PUT) desse agente.
 */
export default function AgenteFormDialog({
  visible,
  onHide,
  onSaved,
  toastRef,
  defaultTipo = 'Cliente',
  defaultFilialId = null,
  agente = null,
}) {
  const [form, setForm] = useState(() => emptyForm(defaultTipo, defaultFilialId));
  const [salvando, setSalvando] = useState(false);
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [filiais, setFiliais] = useState([]);

  useEffect(() => {
    api.get('/empresas/me').then((r) => setEmpresa(normalize(r.data))).catch(() => setEmpresa(null));
    api.get('/filiais').then((r) => setFiliais(normalize(r.data))).catch(() => setFiliais([]));
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (agente) {
      const pessoa = agente.pessoa || inferPessoa(agente.cnpjcpf, 'Juridica');
      const modo = pessoa === 'Fisica' ? 'cpf' : 'cnpj';
      setForm({
        ...emptyForm(defaultTipo, defaultFilialId),
        ...agente,
        pessoa,
        cnpjcpf: formatCnpjCpf(agente.cnpjcpf || '', modo),
        telefone: formatTelefone(agente.telefone || ''),
        filialId: agente.filialId > 0 ? agente.filialId : 0,
      });
    } else {
      setForm(emptyForm(defaultTipo, defaultFilialId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, agente]);

  const filialFormOptions = useMemo(
    () => buildFilialFilterOptions(empresa, filiais, { incluirTodas: false }),
    [empresa, filiais]
  );

  const nomeEmpresaPrincipal = labelEmpresaFilial(empresa, 'Empresa');

  const setCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const consultarCnpjAgente = async () => {
    if (form.pessoa === 'Fisica') {
      toastRef?.current?.show({
        severity: 'info',
        summary: 'Consulta',
        detail: 'Consulta automática disponível apenas para CNPJ (pessoa jurídica).',
      });
      return;
    }
    setConsultandoCnpj(true);
    try {
      const dadosEmpresa = await consultarCnpj(digitsOnly(form.cnpjcpf));
      const campos = camposDaConsultaCnpj(dadosEmpresa);
      if (!campos) {
        toastRef?.current?.show({
          severity: 'warn',
          summary: 'Consulta CNPJ',
          detail: dadosEmpresa?.message || 'Não foi possível encontrar os dados do CNPJ.',
        });
        return;
      }
      setForm((prev) => ({
        ...prev,
        ...campos,
        pessoa: 'Juridica',
        cnpjcpf: formatCnpjCpf(campos.cnpjcpf || prev.cnpjcpf || digitsOnly(form.cnpjcpf), 'cnpj'),
        telefone: formatTelefone(campos.telefone || prev.telefone),
      }));
      if (campos.cidade && campos.uf) {
        try {
          const { data } = await api.get('/util/ibge', { params: { cidade: campos.cidade, uf: campos.uf } });
          const codigo = normalize(data)?.codigoIbge;
          if (codigo) setCampo('codigoIbgeMunicipio', codigo);
        } catch {
          // IBGE opcional
        }
      }
      toastRef?.current?.show({ severity: 'success', summary: 'Consulta CNPJ', detail: 'Dados carregados com sucesso.' });
    } catch (error) {
      toastRef?.current?.show({
        severity: 'error',
        summary: 'Consulta CNPJ',
        detail: error.code === 'INVALID_CNPJ' ? error.message : 'Erro ao consultar CNPJ. Tente novamente.',
      });
    } finally {
      setConsultandoCnpj(false);
    }
  };

  const salvar = async () => {
    const digitos = digitsOnly(form.cnpjcpf);
    const pessoa = inferPessoa(digitos, form.pessoa);
    if (pessoa === 'Fisica' && digitos.length !== 11) {
      toastRef?.current?.show({ severity: 'warn', summary: 'CPF', detail: 'Informe um CPF com 11 dígitos.' });
      return;
    }
    if (pessoa === 'Juridica' && digitos.length !== 14) {
      toastRef?.current?.show({ severity: 'warn', summary: 'CNPJ', detail: 'Informe um CNPJ com 14 dígitos.' });
      return;
    }
    if (!form.razaosocial?.trim()) {
      toastRef?.current?.show({ severity: 'warn', summary: 'Razão social', detail: 'Informe a razão social.' });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        tipo: form.tipo,
        pessoa,
        razaosocial: form.razaosocial,
        fantasia: form.fantasia,
        cnpjcpf: digitos,
        email: form.email,
        emailsAdicionais: form.emailsAdicionais,
        telefone: form.telefone,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento,
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf,
        cep: digitsOnly(form.cep),
        codigoIbgeMunicipio: form.codigoIbgeMunicipio,
        inscricaoMunicipal: form.inscricaoMunicipal,
        inscricaoEstadual: form.inscricaoEstadual,
        retemImpostosFederais: form.retemImpostosFederais,
        filialId: form.filialId > 0 ? form.filialId : null,
      };

      let result;
      if (form.id) {
        const { data } = await api.put(`/agentes/${form.id}`, payload);
        result = normalize(data);
      } else {
        const { data } = await api.post('/agentes', payload);
        result = normalize(data);
      }

      toastRef?.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Agente salvo com sucesso.' });
      onSaved?.(result);
      onHide?.();
    } catch (error) {
      toastRef?.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: error.response?.data?.message || 'Não foi possível salvar o agente.',
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog
      header={form.id ? 'Editar cliente/fornecedor' : 'Novo cliente/fornecedor'}
      visible={visible}
      onHide={onHide}
      style={{ width: '900px' }}
      breakpoints={{ '960px': '95vw' }}
    >
      <div className="agentes-form">
        <div className="nfse-form-row">
          <div className="agentes-field agentes-field-cnpj">
            <label htmlFor="agentefd-cnpjcpf">{form.pessoa === 'Fisica' ? 'CPF' : 'CNPJ'}</label>
            <div className="empresa-ibge-row">
              <InputCnpjCpfCustom
                id="agentefd-cnpjcpf"
                modo={form.pessoa === 'Fisica' ? 'cpf' : 'cnpj'}
                value={form.cnpjcpf}
                onChange={(value) => {
                  const pessoa = inferPessoa(value, form.pessoa);
                  setForm((prev) => ({ ...prev, cnpjcpf: value, pessoa }));
                }}
              />
              <Button
                label="Consultar"
                icon="pi pi-search"
                className="p-button-outlined"
                loading={consultandoCnpj}
                disabled={form.pessoa === 'Fisica'}
                onClick={consultarCnpjAgente}
                tooltip={form.pessoa === 'Fisica' ? 'Consulta Receita só para CNPJ' : 'Consulta CNPJ na Receita Federal'}
              />
            </div>
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field agentes-field-sm">
            <label>Tipo</label>
            <Dropdown value={form.tipo} options={TIPOS} onChange={(e) => setCampo('tipo', e.value)} />
          </div>
          <div className="agentes-field agentes-field-sm">
            <label>Pessoa</label>
            <Dropdown
              value={form.pessoa}
              options={TIPOS_PESSOA}
              onChange={(e) => {
                const pessoa = e.value;
                setForm((prev) => ({
                  ...prev,
                  pessoa,
                  cnpjcpf: formatCnpjCpf(prev.cnpjcpf, pessoa === 'Fisica' ? 'cpf' : 'cnpj'),
                }));
              }}
            />
          </div>
          <div className="agentes-field agentes-field-sm">
            <label>Empresa / Filial</label>
            <Dropdown
              value={form.filialId}
              options={filialFormOptions}
              onChange={(e) => setCampo('filialId', e.value)}
              optionLabel="label"
              filter
              filterBy="search,razao,fantasia,cnpjcpf,label"
              itemTemplate={richItemTemplate}
              valueTemplate={richValueTemplate}
              panelClassName="rich-select-panel"
              placeholder={nomeEmpresaPrincipal}
              className="w-full"
            />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-razaosocial">Razão social</label>
            <InputText id="agentefd-razaosocial" value={form.razaosocial} onChange={(e) => setCampo('razaosocial', e.target.value)} />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-fantasia">Nome fantasia</label>
            <InputText id="agentefd-fantasia" value={form.fantasia} onChange={(e) => setCampo('fantasia', e.target.value)} />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field">
            <label htmlFor="agentefd-email">E-mail</label>
            <InputText id="agentefd-email" value={form.email} onChange={(e) => setCampo('email', e.target.value)} />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-telefone">Telefone</label>
            <InputTelefoneCustom
              id="agentefd-telefone"
              value={form.telefone}
              onChange={(value) => setCampo('telefone', value)}
            />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field agentes-field-full">
            <label htmlFor="agentefd-emails-adicionais">E-mails adicionais (separados por vírgula)</label>
            <InputTextarea
              id="agentefd-emails-adicionais"
              rows={2}
              value={form.emailsAdicionais}
              onChange={(e) => setCampo('emailsAdicionais', e.target.value)}
            />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field">
            <label htmlFor="agentefd-im">Inscrição Municipal</label>
            <InputText id="agentefd-im" value={form.inscricaoMunicipal} onChange={(e) => setCampo('inscricaoMunicipal', e.target.value)} />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-ie">Inscrição Estadual</label>
            <InputText id="agentefd-ie" value={form.inscricaoEstadual} onChange={(e) => setCampo('inscricaoEstadual', e.target.value)} />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field agentes-field-sm">
            <label>CEP</label>
            <InputCepCustom
              value={form.cep}
              onChange={(value) => setCampo('cep', value)}
              onCepDataFetch={(data) => {
                if (data && !data.erro) {
                  setForm((prev) => ({
                    ...prev,
                    logradouro: data.logradouro || prev.logradouro,
                    bairro: data.bairro || prev.bairro,
                    cidade: data.localidade || prev.cidade,
                    uf: data.uf || prev.uf,
                  }));
                }
              }}
            />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-logradouro">Logradouro</label>
            <InputText id="agentefd-logradouro" value={form.logradouro} onChange={(e) => setCampo('logradouro', e.target.value)} />
          </div>
          <div className="agentes-field agentes-field-sm">
            <label htmlFor="agentefd-numero">Número</label>
            <InputText id="agentefd-numero" value={form.numero} onChange={(e) => setCampo('numero', e.target.value)} />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field">
            <label htmlFor="agentefd-complemento">Complemento</label>
            <InputText id="agentefd-complemento" value={form.complemento} onChange={(e) => setCampo('complemento', e.target.value)} />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-bairro">Bairro</label>
            <InputText id="agentefd-bairro" value={form.bairro} onChange={(e) => setCampo('bairro', e.target.value)} />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field">
            <label htmlFor="agentefd-cidade">Cidade</label>
            <InputText id="agentefd-cidade" value={form.cidade} onChange={(e) => setCampo('cidade', e.target.value)} />
          </div>
          <div className="agentes-field agentes-field-sm">
            <label htmlFor="agentefd-uf">UF</label>
            <InputText id="agentefd-uf" value={form.uf} maxLength={2} onChange={(e) => setCampo('uf', e.target.value.toUpperCase())} />
          </div>
          <div className="agentes-field">
            <label htmlFor="agentefd-ibge">Código IBGE</label>
            <InputText id="agentefd-ibge" value={form.codigoIbgeMunicipio} onChange={(e) => setCampo('codigoIbgeMunicipio', e.target.value)} />
          </div>
        </div>

        <div className="nfse-form-row">
          <div className="agentes-field agentes-field-checkbox">
            <Checkbox
              inputId="agentefd-retemImpostosFederais"
              checked={form.retemImpostosFederais}
              onChange={(e) => setCampo('retemImpostosFederais', e.checked)}
            />
            <label htmlFor="agentefd-retemImpostosFederais">Retém impostos federais</label>
          </div>
        </div>
      </div>

      <div className="agentes-dialog-footer">
        <Button label="Cancelar" className="p-button-text" onClick={onHide} />
        <Button label="Salvar" icon="pi pi-save" loading={salvando} onClick={salvar} />
      </div>
    </Dialog>
  );
}
